<template>
  <div class="signup-page">
    <div class="flex justify-content-center align-items-center min-h-screen p-4">
      <Card class="signup-card shadow-4 border-round-xl overflow-hidden backdrop-blur-sm">
        <template #title>
          <div class="signup-header p-5 -m-3 text-white relative overflow-hidden">
            <!-- Animated background elements -->
            <div class="absolute inset-0 bg-gradient-to-r from-blue-400/10 to-blue-600/10 animate-pulse-gentle"></div>
            <div class="absolute top-2 right-4 w-16 h-16 bg-white/5 rounded-full blur-xl"></div>
            <div class="absolute bottom-2 left-4 w-12 h-12 bg-blue-300/10 rounded-full blur-lg"></div>
            
            <div class="flex align-items-center justify-content-center flex-column text-center relative z-10">
              <div class="logo-container mb-4">
                <img :src="Logo" alt="FeedWiz Logo" class="signup-logo animate-float">
              </div>
              <div>
                <h1 class="text-2xl font-bold m-0 mb-1 text-white">Join FeedWiz</h1>
                <p class="text-blue-100 text-sm font-medium m-0">Start transforming your data today</p>
              </div>
            </div>
          </div>
        </template>
        <template #content>
          <div class="p-fluid p-6">
            <!-- Success message -->
            <div v-if="registerSuccess" class="flex flex-column align-items-center gap-4 py-4">
              <i class="pi pi-check-circle text-green-500" style="font-size: 4rem;"></i>
              <h2 class="text-xl font-bold text-gray-800 m-0 text-center">Vérifiez votre email</h2>
              <p class="text-gray-600 text-center m-0">
                Un email de confirmation a été envoyé à <strong>{{ email }}</strong>.<br>
                Cliquez sur le lien dans l'email pour activer votre compte.
              </p>
            </div>

            <!-- Registration form -->
            <form v-else @submit.prevent="handleRegister" class="flex flex-column gap-3">
              <div class="field">
                <label for="name" class="block text-sm font-semibold text-gray-800 mb-2">Full Name</label>
                <IconField iconPosition="left">
                  <InputIcon class="pi pi-user" style="color: #94a3b8;"/>
                  <InputText
                      id="name"
                      v-model="name"
                      :placeholder="namePlaceholder"
                      class="w-full border-round-lg hover:border-blue-400 focus:border-blue-500 transition-colors duration-200"
                      style="padding: 0.75rem 0.75rem 0.75rem 2.5rem;"
                      required
                      aria-required="true"
                  />
                </IconField>
              </div>

              <div class="field">
                <label for="email" class="block text-sm font-semibold text-gray-800 mb-2">Email Address</label>
                <IconField iconPosition="left">
                  <InputIcon class="pi pi-envelope" style="color: #94a3b8;"/>
                  <InputText
                      id="email"
                      v-model="email"
                      type="email"
                      :placeholder="emailPlaceholder"
                      :class="{'p-invalid': registerError}"
                      class="w-full border-round-lg hover:border-blue-400 focus:border-blue-500 transition-colors duration-200"
                      style="padding: 0.75rem 0.75rem 0.75rem 2.5rem;"
                      required
                      aria-required="true"
                  />
                </IconField>
              </div>

              <div class="field">
                <label for="password" class="block text-sm font-semibold text-gray-800 mb-2">Password</label>
                <div class="password-field-wrapper relative">
                  <i class="pi pi-lock password-icon"></i>
                  <Password
                      id="password"
                      v-model="password"
                      :placeholder="passwordPlaceholder"
                      :feedback="false"
                      toggleMask
                      :class="{'p-invalid': registerError || (submitted && password.length < 9)}"
                      class="w-full password-input"
                      inputClass="border-round-lg hover:border-blue-400 focus:border-blue-500 transition-colors duration-200"
                      inputStyle="padding: 0.75rem 0.75rem 0.75rem 2.5rem;"
                      required
                      aria-required="true"
                  />
                </div>
                <small class="text-gray-500 text-xs">Password must be at least 9 characters long</small>
                <small v-if="submitted && password.length < 9" class="p-error block mt-1">Password must be at least 9 characters long</small>
              </div>

              <div v-if="registerError" class="bg-red-50 p-3 border-round-lg border-l-4 border-red-400">
                <small class="p-error font-medium">{{ registerError }}</small>
              </div>

              <div class="field">
                <div class="flex align-items-center">
                  <Checkbox v-model="agreeTerms" inputId="agreeTerms" binary :class="{'p-invalid': submitted && !agreeTerms}" class="mr-2"/>
                  <label for="agreeTerms" class="text-sm font-medium text-slate-600 hover:text-slate-800 cursor-pointer">
                    I agree to the <a href="#" class="text-blue-600 hover:text-blue-800 no-underline font-semibold transition-colors duration-200">Terms of Service</a> and <a href="#" class="text-blue-600 hover:text-blue-800 no-underline font-semibold transition-colors duration-200">Privacy Policy</a>
                  </label>
                </div>
                <small v-if="submitted && !agreeTerms" class="p-error block mt-1">You must agree to the terms to continue</small>
              </div>

              <Button
                  type="submit"
                  label="Create Account"
                  icon="pi pi-arrow-right"
                  class="p-button-primary w-full cta-button"
                  size="large"
                  :loading="loading"
              />

              <div class="text-center mt-4 pt-3 border-t-1 border-gray-100">
                <span class="text-slate-600">Already have an account?</span>
                <router-link to="/login" class="ml-2 text-blue-600 hover:text-blue-800 no-underline font-semibold transition-colors duration-200">Sign In</router-link>
              </div>
            </form>
          </div>
        </template>
      </Card>
    </div>
  </div>
</template>

<script setup>
import { ref, computed } from 'vue';
import { useStore } from 'vuex';
import Card from 'primevue/card';
import InputText from 'primevue/inputtext';
import Password from 'primevue/password';
import Checkbox from 'primevue/checkbox';
import Button from 'primevue/button';
import Logo from '@/assets/feedwiz-logo-white-new-2.png';

const name = ref('');
const email = ref('');
const password = ref('');
const agreeTerms = ref(false);
const submitted = ref(false);
const store = useStore();

const loading = computed(() => store.getters.loading);
const registerError = computed(() => store.getters.registerError);
const registerSuccess = computed(() => store.getters.registerSuccess);

// Static placeholders optimized for mobile-first
const namePlaceholder = 'Enter your name';
const emailPlaceholder = 'Enter your email';
const passwordPlaceholder = 'Create a password';

const handleRegister = async () => {
  submitted.value = true;

  if (!agreeTerms.value) {
    return;
  }

  if (password.value.length < 9) {
    return;
  }

  await store.dispatch('register', {
    name: name.value || undefined,
    email: email.value,
    password: password.value
  })
};
</script>

<style scoped>
/* Mobile First - SignUp Page optimized for iPhone SE (375x667) */
.signup-page {
  min-height: 100vh;
  position: relative;
  overflow: hidden;
  padding: 0.5rem; /* Mobile: compact padding */
}

.signup-page::before {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23ffffff' fill-opacity='0.03'%3E%3Ccircle cx='30' cy='30' r='1'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E");
}

/* Mobile First - Card styling */
.signup-card {
  width: 100%;
  max-width: 100%; /* Mobile: full width */
  margin: 0; /* Mobile: no margin */
  background: rgba(255, 255, 255, 0.95);
  backdrop-filter: blur(20px);
  border: 1px solid rgba(255, 255, 255, 0.2);
  position: relative;
  z-index: 10;
}

/* Mobile First - Logo */
.logo-container {
  position: relative;
  display: flex;
  justify-content: center;
  align-items: center;
}

.signup-logo {
  height: 4rem; /* Mobile: smaller logo */
  width: auto;
  filter: drop-shadow(0 2px 4px rgba(0,0,0,0.1));
}

/* Mobile First - Header */
.signup-header {
  background: linear-gradient(135deg, #4a89dc 0%, #5ca0f2 50%, #67b7f7 100%);
  position: relative;
  padding: 1.5rem !important; /* Mobile: compact header padding */
}

/* Mobile First - Form */
.p-fluid {
  padding: 1rem !important; /* Mobile: compact form padding */
}

.flex.flex-column.gap-3 {
  gap: 0.5rem !important; /* Mobile: smaller gaps */
}

.field label {
  margin-bottom: 0.5rem !important; /* Mobile: compact label margins */
}

/* Mobile First - Terms agreement */
.flex.align-items-center {
  align-items: flex-start !important; /* Mobile: align to top for long text */
  gap: 0.5rem !important;
}

.cta-button {
  position: relative;
  overflow: hidden;
  transform: translateY(0);
  transition: all 0.3s ease;
}

.cta-button:hover {
  transform: translateY(-2px);
  box-shadow: 0 10px 25px rgba(0, 0, 0, 0.15) !important;
}

.cta-button::before {
  content: '';
  position: absolute;
  top: 0;
  left: -100%;
  width: 100%;
  height: 100%;
  background: linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.2), transparent);
  transition: left 0.5s;
}

.cta-button:hover::before {
  left: 100%;
}

.animate-pulse-gentle {
  animation: pulse-gentle 3s ease-in-out infinite;
}

@keyframes pulse-gentle {
  0%, 100% { opacity: 1; }
  50% { opacity: 0.8; }
}

.transition-colors {
  transition: color 0.2s ease, border-color 0.2s ease;
}

.password-field-wrapper {
  position: relative;
}

.password-icon {
  position: absolute;
  left: 0.75rem;
  top: 50%;
  transform: translateY(-50%);
  z-index: 10;
  color: #94a3b8;
  pointer-events: none;
}

.animate-float {
  animation: float 4s ease-in-out infinite;
}

@keyframes float {
  0%, 100% {
    transform: translateY(0px) scale(1);
  }
  50% {
    transform: translateY(-8px) scale(1.02);
  }
}

/* ===== SMALL TABLET (576px+) ===== */
@media (min-width: 576px) {
  .signup-page {
    padding: 0.75rem;
  }

  .signup-card {
    max-width: 480px; /* Small tablet: constrained width */
  }

  .signup-logo {
    height: 5rem; /* Small tablet: larger logo */
  }

  .signup-header {
    padding: 2rem !important; /* Small tablet: more padding */
  }

  .p-fluid {
    padding: 1.25rem !important; /* Small tablet: more form padding */
  }

  .flex.flex-column.gap-3 {
    gap: 0.75rem !important; /* Small tablet: larger gaps */
  }

  .flex.align-items-center {
    align-items: center !important; /* Small tablet: center alignment */
    gap: 0.75rem !important;
  }
}

/* ===== MEDIUM TABLET (768px+) ===== */
@media (min-width: 768px) {
  .signup-page {
    padding: 1rem;
  }

  .signup-card {
    max-width: 520px; /* Medium tablet: wider card */
  }

  .signup-logo {
    height: 6rem; /* Medium tablet: original logo size */
  }

  .field label {
    margin-bottom: 0.75rem !important; /* Medium tablet: more label margin */
  }
}

/* ===== LARGE DESKTOP (992px+) ===== */
@media (min-width: 992px) {
  .signup-page {
    padding: 1.5rem;
  }

  .signup-card {
    max-width: 540px; /* Desktop: full width */
  }

  .p-fluid {
    padding: 1.5rem !important; /* Desktop: original padding */
  }

  .flex.flex-column.gap-3 {
    gap: 1rem !important; /* Desktop: original gaps */
  }
}
</style>
